import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { ProductService } from 'src/app/core/api/product/product.service';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { RewardManagementService } from 'src/app/core/services/reward-management/reward-management.service';
import { TopHeaderService } from 'src/app/core/services/top-header/top-header.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { RewardResponseDTO } from 'src/app/shared/models/dtos/responses/reward-response-dto/reward-response-dto';
import { RewardLabels } from 'src/app/shared/models/reward-labels/reward-labels';
import { RoutesKeys } from 'src/app/utils/routes';
import { DialogModalData } from '../dialog-modal/dialog-modal.component';
import { RewardType } from 'src/app/shared/models/constants/reward-type';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TranslocoService } from '@ngneat/transloco';
import { RewardCannotBeRedeemedException } from 'src/app/shared/errors/reward-service/reward-service-errors';
import { Equipment } from 'src/app/shared/models/dtos/register-form/equipment/equipment';

@Component({
  selector: 'app-reward-card',
  templateUrl: './reward-card.component.html',
  styleUrls: ['./reward-card.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Reward card molecule
 */
export class RewardCardComponent implements OnInit {
  @Input() reward!: RewardResponseDTO;
  @Input() disabled = false;
  @Input() familyId!: number;
  @Output() redeeming = new EventEmitter();
  rewardLabels = new RewardLabels();
  disclaimerLabel = '';
  private _small = false;
  RoutesKeys = RoutesKeys;
  private confirmRedeemDialogData!: DialogModalData;
  private errorDialogData!: DialogModalData;
  private errorEquipmentDialogData!: DialogModalData;
  private errorRedemptionLimitDialogData!: DialogModalData;
  loading = false;
  cost!: number;
  equipments: Equipment[] = [];
  language = '';

  /**
   * @constructor
   */
  constructor(
    // NOSONAR
    public translationService: TranslationService,
    public router: Router,
    public readonly dialog: DialogService,
    public readonly rewardManagementService: RewardManagementService,
    public readonly topHeaderService: TopHeaderService,
    private readonly productService: ProductService,
    private readonly snackbarService: SnackBarService,
    private readonly translocoService: TranslocoService
  ) {}

  /**
   * on init
   */
  ngOnInit() {
    this.language = this.translationService.getActiveLang();
    this.confirmRedeemDialogData = {
      title: 'redeem-reward',
      content: 'redeem-reward-dialog-content',
      successButtonText: 'confirm',
      failButtonText: 'cancel',
    };

    if (this.reward.rewardType === RewardType.CUSTOMER_SERVICE_EXTENSION) {
      this.confirmRedeemDialogData.hasDisclaimer = true;
      this.confirmRedeemDialogData.disclaimer = 'dialog-disclaimer-reward';
      this.confirmRedeemDialogData.contactSsupportDisclaimer =
        'contact-support-reward';
      this.confirmRedeemDialogData.faqLink = 'faq-page';
      this.confirmRedeemDialogData.optionsLabel = 'printer';
      this.confirmRedeemDialogData.content =
        'redeem-reward-dialog-choice-content';
      this.confirmRedeemDialogData.optionsPlaceholder =
        'select-your-printer-serial-number';
      this.productService.getBusinessEquipments().subscribe({
        next: (equipments) => {
          if (equipments.length !== 0) {
            this.equipments = equipments;
          }
        },
        error: () => {
          this.snackbarService.error(this.rewardLabels.errorEquipmentFetch);
        },
      });
    }

    this.prepareModalContent();

    this.translationService.translateObject(this.rewardLabels);
    this.translationService
      .translateString('disclaimer-label', {
        value: this.reward?.redemptionLimit ?? '',
      })
      .then((s) => {
        this.disclaimerLabel = s;
      });
    this.translationService
      .translatePlural(this.confirmRedeemDialogData.content!, this.reward.cost)
      .then((s) => {
        this.confirmRedeemDialogData.content = s;
        this.translationService.translateObject(this.confirmRedeemDialogData);
      });
    this.cost = this.reward.cost;
  }

  /**
   * Translates titles and bodies of all modals
   */
  private prepareModalContent() {
    this.errorDialogData = {
      title: 'error-redeeming-reward-dialog-title',
      content: 'error-redeeming-reward-dialog-content',
      successButtonText: 'ok',
    };
    this.errorEquipmentDialogData = {
      title: 'error-redeeming-reward-dialog-title',
      content: 'error-redeeming-reward-equipment-dialog-content',
      successButtonText: 'ok',
    };
    this.errorRedemptionLimitDialogData = {
      title: 'redemption-limit-title',
      content: 'redemption-limit-body',
      successButtonText: 'ok',
    };
    this.translationService.translateObject(this.errorDialogData);
    this.translationService.translateObject(this.errorEquipmentDialogData);
    this.translationService.translateObject(
      this.errorRedemptionLimitDialogData
    );
  }

  /**
   * Get card size according to flag
   * @return {boolean}
   */
  isSmall(): boolean {
    if (this._small) {
      return true;
    }
    return false;
  }

  /**
   * Decides if the card size is small
   * @return {boolean}
   */
  @Input()
  get small(): boolean {
    return this._small;
  }

  /**
   * Sets this card size as small by using an html attribute
   * @param {any} value
   */
  set small(value: any) {
    this._small = coerceBooleanProperty(value);
  }

  /**
   * Open reward details page
   */
  openDetails() {
    this.router.navigate([RoutesKeys.REWARDS, this.reward.id]);
  }

  /**
   * Opens confirmation dialog asking if user wants to redeem a reward
   */
  openRedeemRewardDialog() {
    if (this.isWarranty && this.equipments.length === 0) {
      this.snackbarService.error(
        this.translocoService.translate('no-equipments-error')
      );
      return;
    }
    if (this.equipments.length > 0) {
      this.confirmRedeemDialogData.options = this.equipments
        .filter((item) => item.family?.id === this.familyId)
        .map((item) => item.serial);
    }
    this.dialog.openDialog(this.confirmRedeemDialogData, (equipmentSerial) => {
      this.redeemReward(equipmentSerial);
    });
  }

  /**
   * Redeems this reward
   * @param {string} equipmentSerial
   */
  async redeemReward(equipmentSerial?: string) {
    this.loading = true;
    this.redeeming.emit(this.loading);
    try {
      const coupon = await this.rewardManagementService.redeemReward(
        this.reward.id,
        this.familyId,
        equipmentSerial
      );
      this.router.navigate([RoutesKeys.COUPONS, coupon.id]);
    } catch (error) {
      this.handleError(error as Error);
    } finally {
      this.loading = false;
      this.redeeming.emit(this.loading);
    }
  }

  /**
   * Decides if user has enough points to redeem reward
   * @return {boolean}
   */
  hasEnoughPoints(): boolean {
    return Boolean(
      this.rewardManagementService.points !== null &&
        this.rewardManagementService.points !== undefined &&
        this.reward.cost <= this.rewardManagementService.points
    );
  }

  /**
   * Decides if redeem reward button is disabled
   * @return {boolean}
   */
  isRedeemDisabled(): boolean {
    return !this.hasEnoughPoints() || this.disabled || this.loading;
  }

  /**
   * Gets redeem reward button label
   * @return {string | HTMLSpanElement}
   */
  getRedeemRewardLabel(): string | HTMLSpanElement {
    const points = this.rewardManagementService.points;
    if (!this.hasEnoughPoints() && points !== null) {
      const missingPoints = this.getMissingPoints();
      return `${missingPoints} ${this.rewardLabels.redeemRewardDisabled}`;
    }
    return this.rewardLabels.redeemReward;
  }

  /**
   * @return {number | undefined}
   */
  getMissingPoints(): number | undefined {
    const points = this.rewardManagementService.points;
    if (points != null) {
      return this.reward.cost - points!;
    }
    return undefined;
  }

  /**
   * Handles possible redeemal errors
   * @param {any} error
   */
  handleError(error: any) {
    if (error instanceof RewardCannotBeRedeemedException) {
      this.dialog.openDialog(
        this.errorEquipmentDialogData,
        undefined,
        undefined,
        600
      );
      return;
    }
    if (error.error.errorCode === 'RWD-RRL-001') {
      this.dialog.openDialog(
        this.errorRedemptionLimitDialogData,
        undefined,
        undefined,
        600
      );
      return;
    }
    this.dialog.openDialog(this.errorDialogData, undefined, undefined, 600);
  }

  /**
   * If this reward is a warranty
   */
  get isWarranty() {
    return this.reward.rewardType === RewardType.CUSTOMER_SERVICE_EXTENSION;
  }

  /**
   * Handles image access error on s3
   */
  handleImageError() {
    this.reward.imgSource = '';
  }
}
